import { useContext, useEffect, useMemo, useState } from 'react';
import {
  BufferGeometry,
  Float32BufferAttribute,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
} from 'three';

import { iiafe } from '../../../../utils/async';
import { renderOrders } from '../../../../viewer/render-orders';
import { parseWorldFile } from '../../../../viewer/tools/orthophoto-comparison-tool';
import { GeodataContext } from '../geodata-state';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';

export const useOrthoPreview = () => {
  const { origo } = useContext(GeodataSpatialReferenceContext);
  const [orthoPreview, setOrthoPreview] = useState<Mesh>();
  const { geodata } = useContext(GeodataContext);

  const pngUrl = useMemo(
    () => geodata.resourceUrls?.find((x) => x.name === 'preview')?.url,
    [geodata],
  );
  const pgwUrl = useMemo(
    () => geodata.resourceUrls?.find((x) => x.name === 'preview.world')?.url,
    [geodata],
  );

  useEffect(() => {
    if (pngUrl && pgwUrl && origo) {
      iiafe(async () => {
        const mesh = await getWebpQuad(pngUrl, pgwUrl, origo);
        setOrthoPreview(mesh);
      });
    }
  }, [pngUrl, pgwUrl, origo]);

  return { orthoPreview };
};

async function getWebpQuad(pngUrl: string, pgwUrl: string, origo: Vector3): Promise<Mesh> {
  const texture = await new TextureLoader().loadAsync(pngUrl);
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;

  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
  });

  const worldFile = await parseWorldFile(pgwUrl);
  const size = new Vector3(
    texture.image.width * worldFile.pixelWidth,
    texture.image.height * worldFile.pixelHeight,
    0,
  );

  // prettier-ignore
  const position = [
    0, 0, 0, // Bottom left.
    0, size.y, 0, // Top left.
    size.x, 0, 0, // Bottom right.
    size.x, size.y, 0, // Top right.
  ];

  const normals = [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1];

  const uvs = [0, 0, 0, 1, 1, 0, 1, 1];

  const indices = [0, 2, 1, 1, 2, 3];

  const geometry = new BufferGeometry();

  geometry.setIndex(indices);
  geometry.setAttribute('position', new Float32BufferAttribute(position, 3));
  geometry.setAttribute('normal', new Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geometry.computeBoundingSphere();

  const mesh = new Mesh(geometry, material);
  mesh.renderOrder = renderOrders.aligner.orthoPreview;
  mesh.position.copy(
    new Vector3(worldFile.topLeftX, worldFile.topLeftY - size.y, 0).sub(origo).setZ(-1000),
  );

  return mesh;
}
